import { Navigate, useLocation, useParams } from 'react-router';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { selectRestaurant } from '../redux/slices/restaurantSlice';
import { fetchSiteContentRequest } from '../redux/slices/siteContentSlice';
import { checkAndValidateEditControl } from '../utils/editControlUtils';
import { getAppMode, isValidTemplate } from '../utils/appConfig';
import { getRestaurantId } from '../api/tokenUtils';
import { useEffect, useMemo } from 'react';

export default function RestaurantGate({
  children,
  requiresRestaurant = true,
  redirectTo = '/select-location',
}: {
  children: React.ReactNode;
  requiresRestaurant?: boolean;
  redirectTo?: string;
}) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { restaurants, selectedRestaurant } = useAppSelector((state) => state.restaurant);
  const { id } = useParams(); // Using the single 'id' parameter from router

  // Use the centralized mode detection
  const mode = getAppMode();
  const isPreviewPath = location.pathname.includes('/preview');
  const isEditPath = location.pathname.includes('/preview/edit');

  // Determine what type of ID we have
  const isTemplatePreview = useMemo(() => {
    return mode === 'preview' && id && isValidTemplate(id);
  }, [mode, id]);

  const isRestaurantEdit = useMemo(() => {
    return mode === 'edit' && id && !isValidTemplate(id);
  }, [mode, id]);

  const isRestaurantMode = useMemo(() => {
    return isRestaurantEdit || (id && !isValidTemplate(id) && (isPreviewPath || isEditPath));
  }, [isRestaurantEdit, id, isPreviewPath, isEditPath]);

  // Handle template preview mode
  useEffect(() => {
    if (isTemplatePreview) {
      console.log('Template preview mode detected:', id);
      dispatch(fetchSiteContentRequest());
    }
  }, [isTemplatePreview, id, dispatch]);

  // Handle restaurant selection from URL (edit mode or restaurant preview)
  useEffect(() => {
    if (isRestaurantMode && id && restaurants?.length) {
      const restaurant = restaurants.find(r => r.restaurant_id === id);
      if (restaurant && selectedRestaurant?.restaurant_id !== restaurant.restaurant_id) {
        console.log('Restaurant mode detected, selecting restaurant:', id);
        dispatch(selectRestaurant(restaurant.restaurant_id));
        dispatch(fetchSiteContentRequest());
        
        // Enable edit controls if in edit mode
        if (mode === 'edit') {
          setTimeout(() => checkAndValidateEditControl(restaurant.restaurant_id), 100);
        }
      }
    }
  }, [isRestaurantMode, id, restaurants, mode, selectedRestaurant, dispatch]);

  // Handle normal mode page refresh with stored restaurant ID (e.g., /menu, /contact, etc.)
  useEffect(() => {
    if (mode === 'normal' && 
        location.pathname !== '/' && 
        !selectedRestaurant && 
        !isTemplatePreview && 
        !isRestaurantMode) {
      
      const storedRestaurantId = getRestaurantId();
      
      if (storedRestaurantId && restaurants?.length) {
        const restaurant = restaurants.find(r => r.restaurant_id === storedRestaurantId);
        if (restaurant) {
          dispatch(selectRestaurant(storedRestaurantId));
          dispatch(fetchSiteContentRequest());
        }
      }
    }
  }, [mode, location.pathname, selectedRestaurant, isTemplatePreview, isRestaurantMode, restaurants, dispatch]);

  // Skip location selector for all preview/edit modes
  if (isTemplatePreview || isRestaurantMode || mode !== 'normal') {
    return <>{children}</>;
  }

  // Auto-select restaurant if only one available (skip selection page)
  if (requiresRestaurant && !selectedRestaurant && location.pathname === '/' && restaurants?.length === 1) {
    dispatch(selectRestaurant(restaurants[0].restaurant_id));
    dispatch(fetchSiteContentRequest());
    return <>{children}</>;
  }

  // Show location selector only if multiple restaurants are available
  if (requiresRestaurant && !selectedRestaurant && location.pathname === '/' && restaurants?.length > 1) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
